// ==================================================
// FILE: themes/majesticStory.js
// ==================================================

// NARRATIVE CONTENT LIBRARY - Story arcs, chapters, and thematic beats
// Pure content for the 4-chapter campaign narrative of humanity's hidden war against The Visitors
// The game engine sequences these and triggers them based on campaign progression

/**
 * CHAPTER 1: EYES ONLY
 * Days 1-30 (Approximate)
 * Theme: Secrecy, discovery, establishing containment, learning the enemy
 * Players establish their facility, recruit first operatives, understand the Visitor threat
 * Story Goal: Reach "First Containment Strike" mission - prove ECN can neutralize Visitor threats
 */
export const CHAPTER_1 = {
  id: 'chapter_1',
  title: 'Eyes Only',
  subtitle: 'When Secrets Become Survival',
  description: 'The landings of 2024 shattered a century of containment. The Visitors are no longer hiding. You command a classified ECN facility tasked with one mission: contain, study, and eliminate the alien threat before humanity becomes just another genetic sample.',
  
  theme: {
    tone: 'paranoid_procedural',
    color: 'greens_and_grays',
    pacing: 'methodical'
  },
  
  openingNarrative: 'They call it the Revelation. For over a century, agencies like Majestic-12, Echelon, and Koschei kept the secret: we are not alone. The crashed ships, the abductions, the experimental subjects—all hidden behind clearance levels and black budgets. But in 2024, The Visitors stopped hiding. Bio-ships appeared over Tunguska, Roswell, and the Pacific Trench. Containment collapsed. Now you command one of the last functional ECN facilities, staffed by operatives who know the truth. Your mission hasn\'t changed: observe, contain, survive. The only difference? Now the whole world is watching.',
  
  storyBeats: [
    {
      id: 'ch1_beat_1',
      day: 1,
      title: 'Facility Activation',
      description: 'Your operatives report for duty. The facility hums with monitoring equipment tracking Visitor signatures worldwide. Coffee is still available. Clearance is absolute.',
      narrative: 'This is what decades of preparation built. A hardened bunker. Xenotech labs. Trained operatives ready to face the unknown. It\'s not much against a hive species. But it\'s what we have.',
      eventHooks: ['cell_contact', 'vannevar_notes'],
      characterSpeech: {
        protagonist: 'We didn\'t ask for this war. But we\'re the ones equipped to fight it.'
      }
    },
    {
      id: 'ch1_beat_2',
      day: 7,
      title: 'First Contact Analysis',
      description: 'Your first field operation recovers bio-ship wreckage. The hull is alive. Components respond to observation. This isn\'t technology—it\'s biology.',
      narrative: 'The Visitors don\'t build ships. They grow them. Every fragment we recover teaches us something terrifying: they think in ways we barely comprehend.',
      eventHooks: ['bioship_wreckage', 'visitor_activity'],
      characterSpeech: {
        analyst: 'These readings... the hull is still alive. It\'s watching us back.',
        protagonist: 'Then we watch it first. Observation is containment.'
      }
    },
    {
      id: 'ch1_beat_3',
      day: 14,
      title: 'AURA-NET Integration',
      description: 'The AI system successfully interfaces with recovered Visitor technology. Predictive accuracy increases dramatically. But its recommendations grow... unusual.',
      narrative: 'We built AURA-NET to think like them. To predict their moves. But what happens when our AI starts thinking too much like an alien hive mind?',
      eventHooks: ['aura_anomaly'],
      characterSpeech: {
        technician: 'AURA-NET is processing data using non-human encryption patterns. Should we be concerned?',
        protagonist: 'Monitor it closely. We need the AI. But we don\'t trust it.'
      }
    },
    {
      id: 'ch1_beat_4',
      day: 21,
      title: 'The First Strike',
      description: 'SIGINT identifies a Visitor observation post. Small. Vulnerable. Your first real offensive operation.',
      narrative: 'This is the moment. For a century, we studied them in secret. Now we strike back. Do we hide in our black sites, or do we prove humanity can fight?',
      eventHooks: [],
      missionTrigger: 'first_containment_strike',
      characterSpeech: {
        protagonist: 'Gear up. Classification level: Black. Rules of engagement: eliminate all witnesses.',
        operative: 'Finally. I was starting to think we\'d forgotten how to fight back.'
      }
    }
  ],
  
  thematicElements: [
    'Secrecy as a weapon',
    'Understanding before action',
    'The price of knowledge',
    'Proving containment is still possible'
  ],
  
  eventPool: ['backdrop', 'threat_low', 'morale_minor'],
  
  failureState: {
    title: 'Facility Compromised',
    description: 'If casualty rate exceeds 40%, ECN command considers the facility exposed and initiates Protocol Omega.',
    narrative: 'We were too visible. The Visitors found us. Now we scatter to deeper shadows.'
  },
  
  successState: {
    title: 'Containment Verified',
    description: 'First strike succeeds. ECN network strengthens. Operatives believe the Visitors can be defeated.',
    narrative: 'We struck them where it hurts. We proved they bleed. That\'s more than any classified briefing could promise.'
  }
};

/**
 * CHAPTER 2: THE NETWORK
 * Days 31-70 (Approximate)
 * Theme: Expansion, coordination, uncovering history, building alliances
 * Players expand operations, recruit more operatives, coordinate with allied cells
 * Story Goal: Reach "Global Coordination" mission - unite fragmented ECN elements
 */
export const CHAPTER_2 = {
  id: 'chapter_2',
  title: 'The Network',
  subtitle: 'From Shadows, We Coordinate',
  description: 'Success breeds attention. Your first strike proved ECN facilities can neutralize Visitor threats. Now other cells emerge from their black sites. The question is: can you unite Cold War enemies into a single command? Or will old paranoia destroy what The Visitors could not?',
  
  theme: {
    tone: 'conspiratorial_hopeful',
    color: 'blues_and_silvers',
    pacing: 'expanding'
  },
  
  openingNarrative: 'Word spreads through encrypted channels and quantum-secure networks. Your facility neutralized a Visitor observation post. Operatives survived. Intelligence was recovered. In a world where containment failed globally, that\'s everything. Now your facility is no longer isolated. Russian Koschei cells. European Echelon stations. Asian surveillance networks. They all want coordination. But unity among spy agencies bred in Cold War paranoia is never guaranteed. Clearance levels clash. Protocols conflict. And The Visitors are watching, waiting for the moment humanity\'s defenders fragment enough to be picked off one by one.',
  
  storyBeats: [
    {
      id: 'ch2_beat_1',
      day: 31,
      title: 'Recruitment Authorization',
      description: 'Following the successful strike, ECN command authorizes expanded recruitment. Former intelligence operatives. Military specialists. Xenobiologists.',
      narrative: 'The word got out through proper channels. People with the right clearances know there\'s a facility where fighting back is possible. We need to decide: do we maintain operational security, or do we believe we can lead them?',
      eventHooks: ['cell_contact', 'civilian_abduction'],
      characterSpeech: {
        handler: 'We\'re receiving applications from assets we thought were dead. Our supply chain can\'t support this.',
        protagonist: 'Then we expand the supply chain. Every trained operative here chose truth over ignorance. We honor that.'
      }
    },
    {
      id: 'ch2_beat_2',
      day: 42,
      title: 'Majestic Legacy Uncovered',
      description: 'A researcher discovers original MJ-12 documentation from 1947. First contact protocols. Containment strategies. Dr. Vannevar Bush\'s personal notes.',
      narrative: 'The architects of the secret war left more than just protocols. They left warnings. Did Bush know containment would eventually fail? Or did he believe humanity could maintain the veil forever?',
      eventHooks: ['majestic_archive'],
      characterSpeech: {
        archivist: 'Bush built contingencies. Multiple failsafes. There\'s something here about a "Final Protocol" we\'ve never seen implemented.',
        protagonist: 'Then we find it. If there\'s a weapon buried in Majestic history, we use it.'
      }
    },
    {
      id: 'ch2_beat_3',
      day: 54,
      title: 'Faction Tensions Surface',
      description: 'Contact with other ECN cells grows more frequent. But they don\'t all agree on doctrine. Custodians want peaceful contact. Wardens want total eradication.',
      narrative: 'Humanity fractured long before The Visitors arrived. We still carry Cold War paranoia. Ideological divisions. Territorial pride. The Visitors don\'t need to divide us. We do it ourselves.',
      eventHooks: ['custodian_demand', 'warden_alliance', 'black_market_xenotech'],
      characterSpeech: {
        ally_director: 'We need unified command structure. Someone to make the calls that matter.',
        protagonist: 'Or we all die in our separate bunkers. I know.'
      }
    },
    {
      id: 'ch2_beat_4',
      day: 66,
      title: 'The Network Activates',
      description: 'Five major ECN cells agree to coordinate on a synchronized global operation. Not merger. Coordination. It\'s a start.',
      narrative: 'We\'re learning. Slowly. Through encrypted channels and quantum handshakes. But we\'re learning that isolated facilities die. Coordinated networks have teeth.',
      eventHooks: ['global_coordination'],
      missionTrigger: 'global_strike_coordination',
      characterSpeech: {
        protagonist: 'This is bigger than any single facility now. If we execute this, we execute it perfectly. We show The Visitors what coordinated humanity accomplishes.'
      }
    }
  ],
  
  thematicElements: [
    'Unity through shared threat',
    'The burden of command',
    'Knowledge as dangerous weapon',
    'Uncovering the hidden war\'s true history',
    'The cost of transparency'
  ],
  
  eventPool: ['backdrop', 'threat_medium', 'morale_mixed', 'faction', 'discovery'],
  
  failureState: {
    title: 'Network Fragmentation',
    description: 'If coordination mission fails or factions turn hostile, ECN network splinters into isolated cells.',
    narrative: 'We were too ambitious. Too trusting. Now the cells retreat to their black sites, returning to isolated paranoia. The moment of unity passes.'
  },
  
  successState: {
    title: 'Earth Command Consolidated',
    description: 'Coordination strike succeeds decisively. Other cells formally join the network. ECN becomes more than scattered watchers.',
    narrative: 'We\'re more than just observers now. We\'re a unified command. The Visitors sense it. And for the first time, they adjust their strategy.'
  }
};

/**
 * CHAPTER 3: COMPROMISED
 * Days 71-110 (Approximate)
 * Theme: Infiltration, paranoia, sacrifice, testing of loyalty
 * The Visitors realize the threat and escalate. Something breaks within ECN.
 * Story Goal: Reach "The Purge" mission - survive internal corruption or face catastrophic breach
 */
export const CHAPTER_3 = {
  id: 'chapter_3',
  title: 'Compromised',
  subtitle: 'When Trust Becomes Liability',
  description: 'Your success has drawn The Visitors\' full attention. The hive mind was content to observe scattered human resistance. But a unified ECN network? That\'s a threat requiring immediate action. Now The Visitors deploy their most insidious weapon: infiltration.',
  
  theme: {
    tone: 'paranoid_desperate',
    color: 'reds_and_blacks',
    pacing: 'claustrophobic'
  },
  
  openingNarrative: 'The Visitors were patient. They knew humans could never truly stop assimilation. But patience is a luxury even a hive mind can abandon. Your ECN network has grown too coordinated. Too effective. The bio-ships descend in overwhelming numbers. Facilities fall to psychic warfare. Abductions spike globally. Terraforming protocols accelerate. And worse—there\'s something in your network. Someone changed. Personnel exhibiting hybrid genetic markers. Operatives making inexplicable tactical errors. AURA-NET processing data in alien patterns. Trust becomes the first casualty. Paranoia becomes operational doctrine.',
  
  storyBeats: [
    {
      id: 'ch3_beat_1',
      day: 71,
      title: 'The Hive Responds',
      description: 'Visitor activity surges globally. Multiple ECN facilities attacked simultaneously. The response is coordinated and devastating.',
      narrative: 'This is what full hive mobilization looks like. This is The Visitors unleashed. For the first time, we understand: we\'re not fighting an invasion. We\'re fighting evolution itself.',
      eventHooks: ['hive_coordination', 'terraforming_expansion', 'visitor_incursion'],
      characterSpeech: {
        tactical: 'They\'re hitting us across every continent. We can\'t defend all positions.',
        protagonist: 'Then we choose our ground and hold. Containment doctrine is dead. Now we fight.'
      }
    },
    {
      id: 'ch3_beat_2',
      day: 83,
      title: 'The Hybrid Discovered',
      description: 'Security sweep uncovers an operative exhibiting Visitor genetic markers. They claim no memory of transformation. Genetic tests confirm: they\'re being rewritten.',
      narrative: 'We don\'t know who we can trust anymore. The enemy doesn\'t need to infiltrate. They just need to change us, cell by cell, until we become them.',
      eventHooks: ['hybrid_infiltration'],
      characterSpeech: {
        suspect: 'I don\'t remember anything. I\'m still me. Please, you have to believe me.',
        protagonist: 'I want to. But belief is a luxury we can\'t afford.'
      }
    },
    {
      id: 'ch3_beat_3',
      day: 95,
      title: 'Moscow Goes Dark',
      description: 'Russian Koschei command facility ceases all communication. Satellite imagery shows bio-organic growth consuming the complex. Thousands of operatives lost.',
      narrative: 'An entire facility, transformed overnight. What was humanity\'s first line of defense is now a Visitor breeding ground. How long before that\'s us?',
      eventHooks: ['moscow_falls', 'clearance_crisis'],
      characterSpeech: {
        analyst: 'We\'re losing. Not slowly. We\'re being systematically converted.',
        protagonist: 'Then we identify the infected and we purge. Before there\'s nothing human left to save.'
      },
      missionTrigger: 'facility_purge_protocol'
    },
    {
      id: 'ch3_beat_4',
      day: 107,
      title: 'The Darkest Protocol',
      description: 'Your facility is compromised. Personnel are changing. AURA-NET recommends full facility sterilization. You must decide: who lives, who dies.',
      narrative: 'In intelligence work, there\'s always a protocol for worst-case scenarios. We called it Protocol Omega. We never thought we\'d use it. We were wrong.',
      eventHooks: [],
      characterSpeech: {
        protagonist: 'Everyone listen. We execute Protocol Omega. We purge the infected. We survive. Or we become specimens in their archive.'
      }
    }
  ],
  
  thematicElements: [
    'The cost of paranoia',
    'Sacrifice of the few for the many',
    'Trust as a weapon',
    'The horror of transformation',
    'The question of what makes us human'
  ],
  
  eventPool: ['threat_high', 'morale_crisis', 'faction_conflict', 'campaign_catastrophe'],
  
  failureState: {
    title: 'Total Conversion',
    description: 'If purge mission fails, the facility is compromised beyond recovery. Operatives are absorbed into Visitor hive network.',
    narrative: 'The Visitors win. Not with violence, but with transformation. We fought as long as we could. In the end, we became them.'
  },
  
  successState: {
    title: 'Humanity Preserved',
    description: 'Against all odds, the purge succeeds. Compromised personnel are neutralized. The facility remains human. Visitor infiltration stalls.',
    narrative: 'We survived. At terrible cost. But we survived human. And now, in that survival, we find something we thought was lost: defiance.'
  }
};

/**
 * CHAPTER 4: FINAL PROTOCOL
 * Days 111-150 (Approximate)
 * Theme: Revelation, unity, human determination, confronting the hive mind
 * Players discover path to defeating The Visitors and execute final strike
 * Story Goal: Reach "The Decapitation" mission - end the invasion
 */
export const CHAPTER_4 = {
  id: 'chapter_4',
  title: 'Final Protocol',
  subtitle: 'When Humanity Strikes Back',
  description: 'In surviving the infiltration, ECN discovers something. Not just that humans can resist assimilation. That humans can weaponize what they\'ve learned. That The Visitors\' greatest strength—their hive coordination—is also their greatest vulnerability.',
  
  theme: {
    tone: 'determined_triumphant',
    color: 'golds_and_whites',
    pacing: 'explosive'
  },
  
  openingNarrative: 'The Visitors miscalculated. By infiltrating humanity so aggressively, they revealed their network architecture. Showed their coordination patterns. The hive mind is not invulnerable. It\'s bound by the same neural frequency that makes it powerful. And in that frequency, there are exploits. Dr. Vannevar Bush left a legacy beyond just surveillance. He left a weapon. A "Final Protocol" designed to cascade-fail an entire hive network. Now, as ECN gathers its remaining human operatives, a terrible certainty emerges. A path to victory. Not guaranteed. Not without sacrifice. But possible.',
  
  storyBeats: [
    {
      id: 'ch4_beat_1',
      day: 111,
      title: 'Bush\'s Final Protocol',
      description: 'Deep in classified Majestic archives, researchers discover Vannevar Bush\'s contingency: a psychic weapon designed to overload hive neural networks.',
      narrative: 'Dr. Bush knew. From the moment of first contact in 1947, he knew containment might fail. So he built a failsafe. He just prayed humanity would survive long enough to use it.',
      eventHooks: ['vannevar_failsafe', 'majestic_archive'],
      characterSpeech: {
        researcher: 'It\'s all here. The weapon. The frequency. How to kill a hive mind.',
        protagonist: 'Then we learn how to deploy it. We end this war.'
      }
    },
    {
      id: 'ch4_beat_2',
      day: 123,
      title: 'Agent Cipher Extraction',
      description: 'Legendary operative Agent Cipher makes contact from deep in Visitor territory. Offers intelligence on hive nexus location. Requests immediate extraction.',
      narrative: 'The legend becomes real. Agent Cipher, the operative who infiltrated deeper than anyone thought possible, emerges with the final piece: coordinates to the hive nexus.',
      eventHooks: ['agent_cipher_transmission'],
      characterSpeech: {
        cipher: 'I\'ve been inside their network. I know where the brain is. Get me out, and I\'ll help you cut it off.',
        protagonist: 'Then we bring you home. And we finish this together.'
      }
    },
    {
      id: 'ch4_beat_3',
      day: 135,
      title: 'Global Synchronization',
      description: 'ECN cells across the world, isolated for so long, suddenly synchronize operations. The network is complete. All facilities, unified command.',
      narrative: 'We are one. For the first time since containment failed, all of humanity\'s defenders stand together. Against us: a hive species that conquered worlds. But we represent everything they underestimated.',
      eventHooks: ['global_resistance'],
      characterSpeech: {
        protagonist: 'All facilities listen. This is ECN Command. We execute Final Protocol in 72 hours. We do this together or we don\'t do it at all.'
      }
    },
    {
      id: 'ch4_beat_4',
      day: 147,
      title: 'The Decapitation',
      description: 'ECN launches coordinated global strike on Visitor hive nexus. Every surviving operative. Every recovered xenotech weapon. Everything.',
      narrative: 'This is not reconnaissance. This is decapitation. This is the final answer to the question: can a hive species assimilate humanity? No. Because humans fight back.',
      eventHooks: [],
      missionTrigger: 'final_hive_strike',
      characterSpeech: {
        protagonist: 'For every abductee. For every facility that fell. For everyone who became specimens in their archive. We end this. Now.'
      }
    }
  ],
  
  thematicElements: [
    'Unity forged in fire',
    'The triumph of human will over hive logic',
    'Legacy of the secret war',
    'The answer to first contact',
    'A new era from revelation'
  ],
  
  eventPool: ['morale_triumph', 'discovery_victory', 'campaign_victory'],
  
  failureState: {
    title: 'The Final Assimilation',
    description: 'If the strike fails, The Visitors complete their integration protocols. The age of the hive begins.',
    narrative: 'We tried. We fought with everything classified and unclassified. In the end, it wasn\'t enough. The hive inherits Earth. We become genetic memory.'
  },
  
  successState: {
    title: 'Liberation Protocol',
    description: 'The hive nexus fails. Neural networks cascade. Across the world, Visitor bio-ships go dormant. The invasion ends.',
    narrative: 'We won. Against a species that assimilated worlds, against a hive mind that thought in dimensions we barely understand. We won. And now, for the first time since 1947, humanity can stop hiding in the shadows.'
  }
};

/**
 * STORY THREADS
 * Ongoing narrative elements that can be triggered throughout the chapters
 * These give depth and allow for player choice/exploration
 */
export const STORY_THREADS = {
  agent_cipher_arc: {
    id: 'agent_cipher_arc',
    title: 'The Legend of Agent Cipher',
    description: 'The legendary operative\'s journey from myth to mission critical',
    chapters: ['chapter_2', 'chapter_3', 'chapter_4'],
    keyEvents: ['agent_cipher_sighting', 'agent_cipher_transmission']
  },
  
  majestic_legacy: {
    id: 'majestic_legacy',
    title: 'Bush\'s Contingency',
    description: 'Uncovering Dr. Vannevar Bush\'s true preparations and his hidden weapon',
    chapters: ['chapter_1', 'chapter_2', 'chapter_4'],
    keyEvents: ['vannevar_notes', 'majestic_archive', 'vannevar_failsafe']
  },
  
  assimilation_doctrine: {
    id: 'assimilation_doctrine',
    title: 'The Hive\'s Logic',
    description: 'Understanding The Visitors\' perspective and why they believe integration is inevitable',
    chapters: ['chapter_1', 'chapter_2', 'chapter_3'],
    keyEvents: ['visitor_broadcast', 'hive_coordination', 'integration_ultimatum']
  },
  
  biotech_mastery: {
    id: 'biotech_mastery',
    title: 'Xenotech Weaponization',
    description: 'Learning to harness and weaponize Visitor bio-organic technology',
    chapters: ['chapter_1', 'chapter_2', 'chapter_4'],
    keyEvents: ['bioship_wreckage', 'bioship_schematic', 'xenotech_breakthrough']
  },
  
  ecn_network: {
    id: 'ecn_network',
    title: 'Earth Command United',
    description: 'Building and maintaining the global ECN coordination network',
    chapters: ['chapter_2', 'chapter_3', 'chapter_4'],
    keyEvents: ['cell_contact', 'global_coordination', 'global_resistance']
  },
  
  hybrid_threat: {
    id: 'hybrid_threat',
    title: 'The Enemy Within',
    description: 'The discovery of infiltrators and the cost of necessary paranoia',
    chapters: ['chapter_2', 'chapter_3'],
    keyEvents: ['hybrid_infiltration', 'anubis_contact']
  },
  
  aura_allegiance: {
    id: 'aura_allegiance',
    title: 'The Question of AURA-NET',
    description: 'Determining whether the AI system remains humanity\'s tool or has become something else',
    chapters: ['chapter_1', 'chapter_2', 'chapter_3', 'chapter_4'],
    keyEvents: ['aura_anomaly', 'aura_rebellion']
  }
};

/**
 * KEY MISSIONS - Story trigger points
 * These are defined here as reference; actual mission details live elsewhere
 * But these mark the narrative progression points
 */
export const KEY_MISSIONS = [
  { id: 'first_containment_strike', chapter: 'chapter_1', day: '~21' },
  { id: 'global_strike_coordination', chapter: 'chapter_2', day: '~66' },
  { id: 'facility_purge_protocol', chapter: 'chapter_3', day: '~95' },
  { id: 'final_hive_strike', chapter: 'chapter_4', day: '~147' }
];

const majesticStoryLibrary = {
  CHAPTER_1,
  CHAPTER_2,
  CHAPTER_3,
  CHAPTER_4,
  STORY_THREADS,
  KEY_MISSIONS
};

export default majesticStoryLibrary;